import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './PendingApproval.css';

const PendingApproval: React.FC = () => {
  const { isAuthenticated, isPending, isActive, loading, logout, userProfile } = useAuth();

  // If not authenticated, redirect to login
  if (!loading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If active, redirect to home
  if (!loading && isAuthenticated && isActive) {
    return <Navigate to="/" replace />;
  }

  // Show loading while checking auth state
  if (loading) {
    return (
      <div className="pending-loading">
        <div className="pending-spinner"></div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="pending-page">
      <div className="pending-container">
        {/* Logo and Header */}
        <div className="pending-logo">
          <div className="pending-logo-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <h1 className="pending-logo-title">BankingLATAM</h1>
        </div>

        {/* Pending Card */}
        <div className="pending-card">
          {/* Pending Icon */}
          <div className="pending-icon-wrapper">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h2 className="pending-title">Account Pending Approval</h2>

          <p className="pending-description">
            Thank you for signing up! Your account is currently pending approval from the
            BankingLATAM team.
          </p>

          {/* User Info */}
          {userProfile && (
            <div className="pending-user-info">
              <p className="pending-user-label">Signed in as</p>
              <p className="pending-user-email">{userProfile.email}</p>
            </div>
          )}

          {/* What to Expect */}
          <div className="pending-info-box">
            <h3 className="pending-info-title">What happens next?</h3>
            <ul className="pending-info-list">
              <li className="pending-info-item">
                <span className="pending-check">✓</span>
                <span>Our team will review your account request</span>
              </li>
              <li className="pending-info-item">
                <span className="pending-check">✓</span>
                <span>You'll be assigned to an organization</span>
              </li>
              <li className="pending-info-item">
                <span className="pending-check">✓</span>
                <span>Once approved, you'll have full access</span>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <p className="pending-contact">
            Questions? Contact us at{' '}
            <a href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL || 'support@bankinglatam.com'}`}>{import.meta.env.VITE_SUPPORT_EMAIL || 'support@bankinglatam.com'}</a>
          </p>

          {/* Logout Button */}
          <button onClick={handleLogout} className="pending-logout-btn">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingApproval;
